import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SEARCH_STOCKS } from '../../graphql/queries/stocks';

const SetStockModal = ({ isOpen, selectedCount, initialQuery = '', hint = '', onClose, onSave, loading }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchStocks, { loading: searching, error }] = useLazyQuery(SEARCH_STOCKS, { fetchPolicy: 'cache-and-network' });

  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setResults([]);
      setSelected(null);
    }
  }, [isOpen, initialQuery]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!isOpen || trimmed.length < 2) {
      setResults([]);
      return undefined;
    }
    const timeout = window.setTimeout(async () => {
      const response = await searchStocks({ variables: { query: trimmed } });
      setResults(response.data?.searchStocks || []);
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [isOpen, query, searchStocks]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selected) return;
    onSave(selected.stockId ? { stockId: selected.stockId } : { symbol: selected.symbol }, selected);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Set stock for {selectedCount} record{selectedCount === 1 ? '' : 's'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <span className="sr-only">Close</span>
            &times;
          </button>
        </div>
        {hint && <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="set-stock-search" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Search by name or ticker
            </label>
            <input
              id="set-stock-search"
              type="text"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelected(null);
              }}
              placeholder="e.g. Alphabet or GOOGL"
              autoFocus
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
            {query.trim().length < 2 ? (
              <p className="p-3 text-sm text-gray-500 dark:text-gray-400">Type at least two characters to search.</p>
            ) : searching && results.length === 0 ? (
              <p className="p-3 text-sm text-gray-500 dark:text-gray-400">Searching…</p>
            ) : error ? (
              <p className="p-3 text-sm text-red-600 dark:text-red-400">Search failed. Try again in a moment.</p>
            ) : results.length === 0 ? (
              <p className="p-3 text-sm text-gray-500 dark:text-gray-400">No instruments found. Try the company name or ticker.</p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700" role="listbox" aria-label="Search results">
                {results.map((stock) => {
                  const isSelected = selected?.symbol === stock.symbol && selected?.exchange === stock.exchange;
                  return (
                    <li key={`${stock.symbol}:${stock.exchange}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => setSelected(stock)}
                        className={`w-full text-left p-3 text-sm ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {stock.symbol} <span className="font-normal text-gray-600 dark:text-gray-300">{stock.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {[stock.exchange, stock.instrumentType?.toUpperCase(), stock.currency].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
              disabled={loading || !selected}
            >
              {loading ? 'Applying…' : 'Apply to selected'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetStockModal;
